import Button from "@/Components/Button/Button";
import Icon, { IconsVariant } from "@/Components/Icon/Icon";
import Spinner from "@/Components/Spinner/Spinner";
import { cn } from "@/shared/utils/cn";
import { ChangeEvent, DragEvent, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ImageCheck,
  ImageGuidelines,
  ImageIssue,
  checkImage,
  formatAspectRatio,
} from "./imageGuidelines";

export interface SelectedImage {
  file: File;
  check: ImageCheck;
}

type Selection = SelectedImage & { id: string; previewUrl: string };

interface ImageUploadProps {
  guidelines: ImageGuidelines;
  onChange: (selection: SelectedImage[]) => void;
  multiple?: boolean;
  /** Only used with multiple. */
  maxFiles?: number;
  disabled?: boolean;
}

export const hasBlockingIssues = (selection: SelectedImage[]) =>
  selection.some((image) => image.check.issues.some((issue) => issue.level === "error"));

const Guideline = ({ icon, children }: { icon: IconsVariant; children: React.ReactNode }) => (
  <li className="flex list-none items-start gap-3">
    <span className="mt-[0.1rem] flex h-[2.2rem] w-[2.2rem] shrink-0 items-center justify-center rounded-full bg-white">
      <Icon className="h-[1.1rem] w-[1.1rem] fill-primary" variants={icon} />
    </span>
    <span className="text-small leading-[2.2rem] text-text">{children}</span>
  </li>
);

const IssueMessage = ({ issue, guidelines: g }: { issue: ImageIssue; guidelines: ImageGuidelines }) => {
  const { t } = useTranslation();
  const isError = issue.level === "error";
  const values: Record<string, string | number> =
    issue.key === "lowResolution"
      ? { width: issue.width, height: issue.height, minWidth: g.minWidth, minHeight: g.minHeight }
      : issue.key === "aspectRatio"
        ? { ratio: issue.ratio }
        : { size: g.maxFileSize / 1024 / 1024 };

  return (
    <div
      role={isError ? "alert" : "status"}
      className={cn(
        "flex items-start gap-3 rounded-[0.8rem] border px-4 py-3 text-small",
        isError ? "border-error bg-error-light text-error" : "border-alert bg-[color-mix(in_srgb,var(--alert)_12%,white)] text-text"
      )}
    >
      <Icon
        className={cn("mt-[0.2rem] h-[1.4rem] w-[1.4rem] shrink-0", isError ? "fill-error" : "fill-alert")}
        variants={isError ? "times" : "exclamation"}
      />
      <span>{t(`productImage.upload.issues.${issue.key}`, values)}</span>
    </div>
  );
};

const formatFileSize = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;

const FileDetails = ({ selection }: { selection: Selection }) => {
  const { width, height } = selection.check;
  return (
    <div className="flex flex-wrap gap-x-6 gap-y-1 text-btn-small text-text-secondary">
      <span className="max-w-full truncate font-semibold text-text">{selection.file.name}</span>
      {width && height && (
        <span>
          {width} × {height}px · {formatAspectRatio(width, height)}
        </span>
      )}
      <span>{formatFileSize(selection.file.size)}</span>
    </div>
  );
};

let nextSelectionId = 0;

const ImageUpload = ({
  guidelines: g,
  onChange,
  multiple = false,
  maxFiles = Infinity,
  disabled = false,
}: ImageUploadProps) => {
  const { t } = useTranslation();
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [selections, setSelections] = useState<Selection[]>([]);
  const limit = multiple ? maxFiles : 1;

  // Release the object urls of removed previews.
  const previewUrls = useRef(new Set<string>());
  useEffect(() => {
    const current = new Set(selections.map((s) => s.previewUrl));
    previewUrls.current.forEach((url) => !current.has(url) && URL.revokeObjectURL(url));
    previewUrls.current = current;
  }, [selections]);
  useEffect(() => () => previewUrls.current.forEach((url) => URL.revokeObjectURL(url)), []);

  const update = (next: Selection[]) => {
    setSelections(next);
    onChange(next.map(({ file, check }) => ({ file, check })));
  };

  const select = async (fileList?: FileList | null) => {
    // Single mode replaces the current image, multiple mode adds up to the limit.
    const kept = multiple ? selections : [];
    const files = Array.from(fileList ?? []).slice(0, Math.max(limit - kept.length, 0));
    if (files.length === 0) return;
    setIsChecking(true);
    const added = await Promise.all(
      files.map(async (file) => {
        const previewUrl = URL.createObjectURL(file);
        const check = await checkImage(file, previewUrl, g);
        return { id: String(nextSelectionId++), file, check, previewUrl };
      })
    );
    setIsChecking(false);
    update([...kept, ...added]);
  };

  const remove = (id: string) => update(selections.filter((s) => s.id !== id));

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    select(event.target.files);
    event.target.value = "";
  };

  const handleDrop = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    setIsDragging(false);
    if (!disabled) select(event.dataTransfer.files);
  };

  const input = (
    <input
      ref={inputRef}
      id={inputId}
      type="file"
      className="sr-only"
      accept={g.acceptedTypes.join(",")}
      multiple={multiple}
      onChange={handleChange}
      disabled={disabled}
    />
  );

  const showGuidelines = Boolean(g.recommendedWidth || g.minWidth || g.tips?.length);
  const canAddMore = selections.length < limit;
  const single = !multiple ? selections[0] : undefined;

  const dropzone = (
    <label
      htmlFor={inputId}
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
      className={cn(
        "flex w-full cursor-pointer flex-col items-center justify-center gap-3 rounded-[1rem] border-2 border-dashed px-8 text-center transition-colors",
        "focus-within:border-primary focus-within:bg-light",
        selections.length > 0 ? "min-h-[14rem]" : "min-h-[30rem]",
        isDragging ? "border-primary bg-light" : "border-border bg-white hover:border-primary hover:bg-light"
      )}
    >
      <span className="flex h-[5.6rem] w-[5.6rem] items-center justify-center rounded-full bg-light">
        <Icon className="h-[2.2rem] w-[2.2rem] fill-primary" variants="download" />
      </span>
      <span className="text-smaller font-semibold text-text">
        {t(multiple ? "imageUpload.dropTitleMultiple" : "productImage.upload.dropTitle")}
      </span>
      <span className="text-btn-small text-text-secondary">
        {t(g.dropHintKey ?? "imageUpload.dropHint", { size: g.maxFileSize / 1024 / 1024 })}
      </span>
      {input}
    </label>
  );

  const renderSingle = (selection: Selection) => {
    const { width, height } = selection.check;
    const hasDimensions = Boolean(width && height);
    const ratio = g.aspectRatio ?? 1;
    const isLandscape = hasDimensions && width / height > ratio;
    const isCropped = selection.check.issues.some((issue) => issue.key === "aspectRatio");

    return (
      <>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
          {/* Full photo with the area that will be visible after cropping. */}
          <div className="flex min-w-0 flex-1 items-center justify-center rounded-[1rem] bg-light p-3">
            <div
              className="relative overflow-hidden rounded-[0.6rem]"
              style={
                hasDimensions
                  ? { aspectRatio: `${width} / ${height}`, width: `min(100%, ${(32 * width) / height}rem)` }
                  : undefined
              }
            >
              <img src={selection.previewUrl} alt="" className="block h-full w-full object-contain" />
              {isCropped && (
                <div
                  aria-hidden="true"
                  className={cn(
                    "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-[0.4rem] border-2 border-white shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]",
                    isLandscape ? "h-full" : "w-full"
                  )}
                  style={{ aspectRatio: ratio }}
                />
              )}
            </div>
          </div>

          {g.aspectRatio && (
            <div className="flex shrink-0 flex-row items-center gap-4 sm:flex-col sm:items-start">
              <img
                src={selection.previewUrl}
                alt=""
                className="w-[10rem] rounded-[0.5rem] object-cover shadow-[0_1px_3px_rgba(0,0,0,0.15)]"
                style={{ aspectRatio: g.aspectRatio }}
              />
              <span className="max-w-[12rem] text-btn-small text-text-secondary">
                {t("productImage.upload.menuPreview")}
              </span>
            </div>
          )}
        </div>

        <FileDetails selection={selection} />

        {selection.check.issues.length === 0 ? (
          <div role="status" className="flex items-center gap-3 rounded-[0.8rem] border border-success px-4 py-3 text-small text-text">
            <Icon className="h-[1.4rem] w-[1.4rem] fill-success" variants="check" />
            {t("productImage.upload.allGood")}
          </div>
        ) : (
          selection.check.issues.map((issue) => <IssueMessage key={issue.key} issue={issue} guidelines={g} />)
        )}

        <div className="flex gap-4">
          <Button type="button" size="small" variant="secondary" isDisabled={disabled} onClick={() => inputRef.current?.click()}>
            {t("imageLibrary.upload.replace")}
          </Button>
          <Button type="button" size="small" variant="danger" isDisabled={disabled} onClick={() => remove(selection.id)}>
            {t("imageLibrary.upload.remove")}
          </Button>
        </div>
        {input}
      </>
    );
  };

  const renderMultiple = () => (
    <>
      <ul className="m-0 flex flex-col gap-4 p-0" aria-label={t("imageUpload.selectedCount", { count: selections.length })}>
        {selections.map((selection) => (
          <li key={selection.id} className="flex list-none items-start gap-4 rounded-[1rem] border border-border p-3">
            <img
              src={selection.previewUrl}
              alt=""
              className="h-[8rem] w-[8rem] shrink-0 rounded-[0.5rem] bg-light object-cover"
            />
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <FileDetails selection={selection} />
              {selection.check.issues.map((issue) => (
                <IssueMessage key={issue.key} issue={issue} guidelines={g} />
              ))}
            </div>
            <Button
              type="button"
              variant="icon"
              icon="trash"
              aria-label={t("imageLibrary.upload.remove")}
              isDisabled={disabled}
              onClick={() => remove(selection.id)}
            />
          </li>
        ))}
      </ul>
      {canAddMore ? (
        dropzone
      ) : (
        <div role="status" className="text-small text-text-secondary">
          {t("imageUpload.maxReached", { count: limit })}
        </div>
      )}
    </>
  );

  return (
    <div className={cn("grid grid-cols-1 gap-8", showGuidelines && "md:grid-cols-[1fr_1.4fr]")}>
      {showGuidelines && (
        <aside className="rounded-[1rem] bg-light p-6">
          {g.guidelinesTitleKey && (
            <div className="mb-4 text-smaller font-semibold text-text">{t(g.guidelinesTitleKey)}</div>
          )}
          <ul className="m-0 flex flex-col gap-3 p-0">
            {g.recommendedWidth && g.recommendedHeight && (
              <Guideline icon="fullscreen">
                {t("productImage.upload.guidelines.ratio", {
                  width: g.recommendedWidth,
                  height: g.recommendedHeight,
                })}
              </Guideline>
            )}
            {g.minWidth && g.minHeight && (
              <Guideline icon="search_plus">
                {t("productImage.upload.guidelines.minimum", { width: g.minWidth, height: g.minHeight })}
              </Guideline>
            )}
            <Guideline icon="clipboardcheck">
              {t("productImage.upload.guidelines.format", { size: g.maxFileSize / 1024 / 1024 })}
            </Guideline>
            {g.tips?.map(({ icon, key }) => (
              <Guideline key={key} icon={icon}>
                {t(key)}
              </Guideline>
            ))}
          </ul>
        </aside>
      )}

      <div className="flex min-w-0 flex-col gap-4">
        {isChecking ? (
          <div className="flex min-h-[14rem] w-full items-center justify-center rounded-[1rem] border border-border bg-light">
            <Spinner size="0.3rem" />
          </div>
        ) : multiple ? (
          renderMultiple()
        ) : single ? (
          renderSingle(single)
        ) : (
          dropzone
        )}
      </div>
    </div>
  );
};

export default ImageUpload;
